import { ReadonlyVec2, vec3 } from 'gl-matrix';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { View } from '~/engine/view/View';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import { AdaptiveBrushCursorAction } from '../../actions/contouring-actions/AdaptiveBrushCursorAction';
import { AdaptiveBrushDrawAction } from '../../actions/contouring-actions/AdaptiveBrushDrawAction';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { MOUSE_BUTTONS_LEFT_MASK, MOUSE_BUTTONS_NONE } from '../../MouseButton';
import { BrushTool } from './BrushTool';

/**
 * {@link AdaptiveBrushTool} is a {@link BrushTool} that allows the user
 * to draw on the {@link RegionOfInterest} using an adaptive brush.\
 * The brush is a circle with a diameter of
 * {@link AdaptiveBrushTool.brushSize} millimeters.\
 * It adapts to an image's intensity values and is used to draw on
 * the {@link RegionOfInterest} using the
 * {@link AdaptiveBrushDrawAction}.
 */
export class AdaptiveBrushTool extends BrushTool {
    /**
     * Method to create a new {@link AdaptiveBrushTool} instance.
     */
    public constructor(
        private readonly image: ImageObject,
        protected readonly structure: RegionOfInterest,
        erase: boolean,
        brushDiameter: number,
        brushSizeChangedCallback: (brushSize: number) => void
    ) {
        super(erase, brushDiameter, structure, brushSizeChangedCallback);
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (buttons === MOUSE_BUTTONS_NONE) {
            if (this._drawState) {
                this._drawState.clearMemory();
                this._drawState = undefined;
            }
            if (
                view instanceof PlaneView &&
                view.label === Orientations.TRANSVERSAL_ORIENTATION &&
                this.structure.canModify &&
                view.type === PlaneViewType.Primary
            ) {
                if (!this._cursorAction) {
                    this.resetCursorAction();
                }
                return (
                    this._cursorAction?.onMouseMove(
                        coordinates,
                        buttons,
                        view
                    ) ??
                    new EventHandlerResult(
                        EventHandlerResultType.EVENT_NOT_HANDLED
                    )
                );
            }
        }
        if (buttons === MOUSE_BUTTONS_LEFT_MASK) {
            if (this._drawState) {
                return this._drawState.onMouseMove(coordinates, buttons, view);
            }
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Method to create a {@link StaticBrushCursorAction} instance, which is the type of {@link CursorAction} used by {@link StaticBrushTool}.
     * @returns A new {@link StaticBrushCursorAction} instance, with diameter set to {@link StaticBrushTool.diameter}.
     */
    protected resetCursorAction(): void {
        // Use a distinct red color when erasing so the user can clearly
        // distinguish erase mode from draw mode at a glance.
        const cursorColor = this._erase
            ? vec3.fromValues(1, 0, 0)
            : this.structure.color;
        this._cursorAction = new AdaptiveBrushCursorAction(
            this.image,
            this._diameter,
            cursorColor
        );
    }

    protected resetDrawAction(): void {
        this._drawAction = new AdaptiveBrushDrawAction(
            this.structure,
            this.image,
            this._diameter,
            this._erase,
            0,
            1
        );
    }
}
